import { useState, useEffect, useRef } from "react";

const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=EB+Garamond:ital,wght@0,400;0,500;0,600;1,400;1,500&family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;1,300;1,400&family=Bebas+Neue&family=DM+Mono:wght@300;400;500&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --ink: #0A0A0A;
    --rule: #1a1a1a;
    --gold: #B8860B;
    --gold-lt: #D4A017;
    --gold-dim: rgba(184,134,11,0.15);
    --muted: #444;
    --faint: #888;
    --surface: #F7F5F0;
    --white: #FEFEFE;
    --red: #8B0000;
    --sage: #4A6741;
    --border: rgba(10,10,10,0.18);
    --shadow: 0 2px 24px rgba(0,0,0,0.08);
  }

  body {
    background: var(--surface);
    color: var(--ink);
    font-family: 'EB Garamond', serif;
    font-size: 17px;
    line-height: 1.6;
    min-height: 100vh;
  }

  /* ── LANDING / FUNNEL SELECTOR ── */
  .landing {
    min-height: 100vh;
    background: var(--ink);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    padding: 2rem;
  }
  .landing::before {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      0deg, transparent, transparent 59px,
      rgba(184,134,11,0.07) 59px, rgba(184,134,11,0.07) 60px
    ), repeating-linear-gradient(
      90deg, transparent, transparent 59px,
      rgba(184,134,11,0.07) 59px, rgba(184,134,11,0.07) 60px
    );
  }
  .landing-inner {
    position: relative;
    z-index: 1;
    text-align: center;
    max-width: 860px;
    width: 100%;
  }
  .landing-rule {
    width: 60px; height: 1px;
    background: var(--gold);
    margin: 0 auto 1.5rem;
  }
  .landing-eyebrow {
    font-family: 'DM Mono', monospace;
    font-size: 0.7rem;
    letter-spacing: 0.22em;
    color: var(--gold);
    text-transform: uppercase;
    margin-bottom: 1rem;
  }
  .landing-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: clamp(2.8rem, 6vw, 5.2rem);
    font-weight: 300;
    color: var(--white);
    line-height: 1.05;
    margin-bottom: 0.4rem;
    letter-spacing: -0.01em;
  }
  .landing-title em {
    font-style: italic;
    color: var(--gold-lt);
  }
  .landing-tagline {
    font-family: 'EB Garamond', serif;
    font-style: italic;
    font-size: 1.15rem;
    color: rgba(254,254,254,0.55);
    margin-bottom: 3rem;
  }
  .landing-divider {
    display: flex; align-items: center; gap: 1rem;
    margin-bottom: 2.5rem;
  }
  .landing-divider::before, .landing-divider::after {
    content: ''; flex: 1; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(184,134,11,0.4), transparent);
  }
  .landing-divider span {
    font-family: 'DM Mono', monospace;
    font-size: 0.65rem;
    letter-spacing: 0.2em;
    color: var(--gold);
    text-transform: uppercase;
  }
  .funnels {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1px;
    background: rgba(184,134,11,0.2);
    border: 1px solid rgba(184,134,11,0.2);
    width: 100%;
    max-width: 760px;
    margin: 0 auto;
  }
  @media (max-width: 580px) { .funnels { grid-template-columns: 1fr; } }
  .funnel-card {
    background: rgba(10,10,10,0.92);
    padding: 2rem 1.8rem;
    cursor: pointer;
    transition: background 0.2s, transform 0.15s;
    text-align: left;
    border: none;
    outline: none;
    position: relative;
    overflow: hidden;
  }
  .funnel-card::after {
    content: '';
    position: absolute;
    bottom: 0; left: 0; right: 0;
    height: 2px;
    background: var(--gold);
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.25s ease;
  }
  .funnel-card:hover { background: rgba(184,134,11,0.08); }
  .funnel-card:hover::after { transform: scaleX(1); }
  .funnel-icon {
    font-size: 1.5rem;
    margin-bottom: 0.75rem;
    display: block;
  }
  .funnel-label {
    font-family: 'DM Mono', monospace;
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    color: var(--gold);
    text-transform: uppercase;
    margin-bottom: 0.3rem;
  }
  .funnel-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.45rem;
    font-weight: 500;
    color: var(--white);
    margin-bottom: 0.5rem;
  }
  .funnel-desc {
    font-family: 'EB Garamond', serif;
    font-style: italic;
    font-size: 0.9rem;
    color: rgba(254,254,254,0.45);
    line-height: 1.5;
  }
  .funnel-arrow {
    position: absolute;
    top: 1.5rem; right: 1.5rem;
    color: var(--gold);
    font-size: 1rem;
    opacity: 0;
    transform: translateX(-4px);
    transition: all 0.2s;
  }
  .funnel-card:hover .funnel-arrow { opacity: 1; transform: translateX(0); }

  /* ── PORTAL SHELL ── */
  .portal {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    background: var(--surface);
    animation: fadeIn 0.35s ease;
  }
  @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }

  .portal-header {
    background: var(--ink);
    border-bottom: 1px solid rgba(184,134,11,0.25);
    padding: 0 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 56px;
    position: sticky; top: 0; z-index: 100;
  }
  .portal-wordmark {
    display: flex; align-items: center; gap: 0.75rem;
  }
  .portal-wordmark-text {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.15rem;
    font-weight: 500;
    color: var(--white);
    letter-spacing: 0.01em;
  }
  .portal-wordmark-badge {
    font-family: 'DM Mono', monospace;
    font-size: 0.58rem;
    letter-spacing: 0.15em;
    color: var(--gold);
    text-transform: uppercase;
    border: 1px solid rgba(184,134,11,0.3);
    padding: 2px 6px;
  }
  .portal-back {
    font-family: 'DM Mono', monospace;
    font-size: 0.62rem;
    letter-spacing: 0.15em;
    color: var(--gold);
    text-transform: uppercase;
    background: none;
    border: 1px solid rgba(184,134,11,0.3);
    padding: 5px 12px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .portal-back:hover { background: var(--gold-dim); }

  .portal-nav {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 0 2rem;
    display: flex;
    gap: 0;
    overflow-x: auto;
  }
  .nav-tab {
    font-family: 'DM Mono', monospace;
    font-size: 0.62rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--faint);
    padding: 0.9rem 1.2rem;
    border: none;
    background: none;
    cursor: pointer;
    border-bottom: 2px solid transparent;
    white-space: nowrap;
    transition: all 0.2s;
  }
  .nav-tab:hover { color: var(--ink); }
  .nav-tab.active {
    color: var(--gold);
    border-bottom-color: var(--gold);
  }

  .portal-content {
    flex: 1;
    padding: 2.5rem 2rem;
    max-width: 1100px;
    margin: 0 auto;
    width: 100%;
  }

  /* ── SECTION HEADERS ── */
  .section-header {
    margin-bottom: 2rem;
  }
  .section-eyebrow {
    font-family: 'DM Mono', monospace;
    font-size: 0.62rem;
    letter-spacing: 0.2em;
    color: var(--gold);
    text-transform: uppercase;
    margin-bottom: 0.4rem;
  }
  .section-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 2.2rem;
    font-weight: 400;
    color: var(--ink);
    line-height: 1.1;
    margin-bottom: 0.4rem;
  }
  .section-sub {
    font-style: italic;
    color: var(--muted);
    font-size: 1rem;
  }
  .rule-gold {
    width: 40px; height: 1px;
    background: var(--gold);
    margin: 1rem 0;
  }

  /* ── KPI CARDS ── */
  .kpi-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
    gap: 1px;
    background: var(--border);
    border: 1px solid var(--border);
    margin-bottom: 2rem;
  }
  .kpi-card {
    background: var(--white);
    padding: 1.4rem 1.2rem;
  }
  .kpi-label {
    font-family: 'DM Mono', monospace;
    font-size: 0.6rem;
    letter-spacing: 0.18em;
    color: var(--faint);
    text-transform: uppercase;
    margin-bottom: 0.5rem;
  }
  .kpi-value {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.4rem;
    color: var(--ink);
    line-height: 1;
    margin-bottom: 0.25rem;
  }
  .kpi-value.gold { color: var(--gold); }
  .kpi-value.sage { color: var(--sage); }
  .kpi-value.red { color: var(--red); }
  .kpi-delta {
    font-family: 'DM Mono', monospace;
    font-size: 0.65rem;
    color: var(--sage);
  }
  .kpi-delta.neg { color: var(--red); }

  /* ── TABLES ── */
  .data-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.92rem;
    margin-bottom: 2rem;
  }
  .data-table th {
    font-family: 'DM Mono', monospace;
    font-size: 0.58rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: var(--faint);
    padding: 0.6rem 1rem;
    text-align: left;
    border-bottom: 1px solid var(--border);
    background: var(--white);
  }
  .data-table td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid rgba(10,10,10,0.06);
    color: var(--ink);
    vertical-align: middle;
  }
  .data-table tr:hover td { background: rgba(184,134,11,0.04); }

  /* ── BADGES ── */
  .badge {
    display: inline-block;
    font-family: 'DM Mono', monospace;
    font-size: 0.58rem;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    padding: 2px 8px;
    border-radius: 0;
  }
  .badge-green { background: rgba(74,103,65,0.12); color: var(--sage); border: 1px solid rgba(74,103,65,0.3); }
  .badge-gold { background: var(--gold-dim); color: var(--gold); border: 1px solid rgba(184,134,11,0.3); }
  .badge-red { background: rgba(139,0,0,0.08); color: var(--red); border: 1px solid rgba(139,0,0,0.2); }
  .badge-gray { background: rgba(10,10,10,0.06); color: var(--muted); border: 1px solid var(--border); }

  /* ── CARDS ── */
  .card {
    background: var(--white);
    border: 1px solid var(--border);
    padding: 1.5rem;
    margin-bottom: 1.2rem;
  }
  .card-title {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.2rem;
    font-weight: 500;
    color: var(--ink);
    margin-bottom: 0.5rem;
  }
  .card-meta {
    font-family: 'DM Mono', monospace;
    font-size: 0.62rem;
    letter-spacing: 0.12em;
    color: var(--faint);
    text-transform: uppercase;
    margin-bottom: 0.75rem;
  }
  .card-body { color: var(--muted); font-size: 0.95rem; line-height: 1.6; }

  /* ── TWO-COL GRID ── */
  .two-col { display: grid; grid-template-columns: 1fr 1fr; gap: 1.5rem; margin-bottom: 2rem; }
  @media (max-width: 680px) { .two-col { grid-template-columns: 1fr; } }
  .three-col { display: grid; grid-template-columns: repeat(3, 1fr); gap: 1.2rem; margin-bottom: 2rem; }
  @media (max-width: 780px) { .three-col { grid-template-columns: 1fr 1fr; } }
  @media (max-width: 480px) { .three-col { grid-template-columns: 1fr; } }

  /* ── PROGRESS BAR ── */
  .progress-wrap { margin-bottom: 1rem; }
  .progress-label {
    display: flex; justify-content: space-between;
    font-family: 'DM Mono', monospace;
    font-size: 0.62rem;
    letter-spacing: 0.1em;
    color: var(--muted);
    margin-bottom: 4px;
  }
  .progress-bar {
    height: 4px; background: rgba(10,10,10,0.08);
    position: relative; overflow: hidden;
  }
  .progress-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--gold), var(--gold-lt));
    transition: width 0.8s ease;
  }
  .progress-fill.green { background: linear-gradient(90deg, var(--sage), #6A9A5E); }

  /* ── FORM ELEMENTS ── */
  .form-group { margin-bottom: 1.2rem; }
  .form-label {
    font-family: 'DM Mono', monospace;
    font-size: 0.62rem;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: var(--muted);
    display: block;
    margin-bottom: 0.4rem;
  }
  .form-input, .form-select, .form-textarea {
    width: 100%;
    padding: 0.65rem 0.9rem;
    border: 1px solid var(--border);
    background: var(--white);
    font-family: 'EB Garamond', serif;
    font-size: 1rem;
    color: var(--ink);
    outline: none;
    transition: border-color 0.2s;
    -webkit-appearance: none;
  }
  .form-input:focus, .form-select:focus, .form-textarea:focus {
    border-color: var(--gold);
    box-shadow: 0 0 0 2px rgba(184,134,11,0.1);
  }
  .form-textarea { min-height: 100px; resize: vertical; }

  /* ── BUTTONS ── */
  .btn {
    font-family: 'DM Mono', monospace;
    font-size: 0.65rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    padding: 0.7rem 1.5rem;
    border: none;
    cursor: pointer;
    transition: all 0.2s;
    display: inline-block;
  }
  .btn-primary {
    background: var(--ink);
    color: var(--gold);
    border: 1px solid var(--ink);
  }
  .btn-primary:hover { background: var(--rule); }
  .btn-gold {
    background: var(--gold);
    color: var(--ink);
  }
  .btn-gold:hover { background: var(--gold-lt); }
  .btn-outline {
    background: transparent;
    color: var(--ink);
    border: 1px solid var(--border);
  }
  .btn-outline:hover { border-color: var(--gold); color: var(--gold); }

  /* ── TIMELINE ── */
  .timeline { position: relative; padding-left: 2rem; }
  .timeline::before {
    content: ''; position: absolute;
    left: 0; top: 4px; bottom: 0;
    width: 1px; background: var(--border);
  }
  .timeline-item { position: relative; margin-bottom: 1.5rem; }
  .timeline-dot {
    position: absolute;
    left: -2rem; top: 4px;
    width: 8px; height: 8px;
    background: var(--gold);
    border-radius: 50%;
    transform: translateX(-3.5px);
  }
  .timeline-date {
    font-family: 'DM Mono', monospace;
    font-size: 0.6rem;
    letter-spacing: 0.12em;
    color: var(--faint);
    text-transform: uppercase;
    margin-bottom: 0.2rem;
  }
  .timeline-text { font-size: 0.95rem; color: var(--muted); }

  /* ── HERO STRIP ── */
  .hero-strip {
    background: var(--ink);
    color: var(--white);
    padding: 2.5rem 2rem;
    margin-bottom: 2rem;
    position: relative;
    overflow: hidden;
  }
  .hero-strip::before {
    content: '';
    position: absolute; right: -40px; top: -40px;
    width: 200px; height: 200px;
    border: 1px solid rgba(184,134,11,0.15);
    border-radius: 50%;
  }
  .hero-strip::after {
    content: '';
    position: absolute; right: 20px; top: 20px;
    width: 120px; height: 120px;
    border: 1px solid rgba(184,134,11,0.1);
    border-radius: 50%;
  }
  .hero-strip h2 {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.8rem;
    font-weight: 400;
    margin-bottom: 0.4rem;
  }
  .hero-strip p {
    font-style: italic;
    color: rgba(254,254,254,0.55);
    font-size: 0.95rem;
  }

  /* ── STAT ROW ── */
  .stat-row {
    display: flex; gap: 0; 
    border: 1px solid var(--border);
    background: var(--border);
    margin-bottom: 2rem;
    overflow-x: auto;
  }
  .stat-cell {
    flex: 1; min-width: 110px;
    background: var(--white);
    padding: 1.2rem 1rem;
    text-align: center;
  }
  .stat-cell-val {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2rem;
    color: var(--gold);
  }
  .stat-cell-lbl {
    font-family: 'DM Mono', monospace;
    font-size: 0.58rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--faint);
    margin-top: 2px;
  }

  /* ── MESSAGE THREAD ── */
  .message { padding: 1rem; border-bottom: 1px solid var(--border); }
  .message:last-child { border-bottom: none; }
  .message-from {
    font-family: 'DM Mono', monospace;
    font-size: 0.6rem;
    letter-spacing: 0.12em;
    color: var(--gold);
    text-transform: uppercase;
    margin-bottom: 0.25rem;
  }
  .message-body { font-size: 0.95rem; color: var(--muted); }
  .message-time { font-family: 'DM Mono', monospace; font-size: 0.58rem; color: var(--faint); margin-top: 4px; }

  /* ── DONATION TIERS ── */
  .tier-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1px; background: var(--border); border: 1px solid var(--border); margin-bottom: 2rem; }
  @media (max-width: 600px) { .tier-grid { grid-template-columns: 1fr; } }
  .tier-card {
    background: var(--white);
    padding: 1.8rem 1.4rem;
    position: relative;
  }
  .tier-card.featured { background: var(--ink); }
  .tier-name {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.1rem;
    font-weight: 600;
    color: var(--ink);
    margin-bottom: 0.25rem;
  }
  .tier-card.featured .tier-name { color: var(--gold-lt); }
  .tier-amount {
    font-family: 'Bebas Neue', sans-serif;
    font-size: 2.6rem;
    color: var(--gold);
    line-height: 1;
    margin-bottom: 0.75rem;
  }
  .tier-perks { list-style: none; }
  .tier-perks li {
    font-size: 0.88rem;
    color: var(--muted);
    padding: 0.25rem 0;
    border-bottom: 1px solid rgba(10,10,10,0.06);
    display: flex; gap: 0.5rem; align-items: baseline;
  }
  .tier-card.featured .tier-perks li { color: rgba(254,254,254,0.6); border-color: rgba(254,254,254,0.08); }
  .tier-perks li::before { content: '—'; color: var(--gold); font-size: 0.7rem; }

  .closing-block {
    background: var(--ink);
    border: 1px solid rgba(184,134,11,0.3);
    padding: 2rem;
    text-align: center;
    margin-top: 2rem;
  }
  .closing-block p {
    font-family: 'Cormorant Garamond', serif;
    font-size: 1.3rem;
    font-style: italic;
    color: rgba(254,254,254,0.7);
    margin-bottom: 1rem;
  }

  .portal-footer {
    background: var(--ink);
    border-top: 1px solid rgba(184,134,11,0.15);
    padding: 1rem 2rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  .footer-copy {
    font-family: 'DM Mono', monospace;
    font-size: 0.58rem;
    letter-spacing: 0.12em;
    color: rgba(254,254,254,0.3);
  }
  .footer-links { display: flex; gap: 1.2rem; }
  .footer-link {
    font-family: 'DM Mono', monospace;
    font-size: 0.58rem;
    letter-spacing: 0.12em;
    color: var(--gold);
    text-transform: uppercase;
    text-decoration: none;
    cursor: pointer;
    background: none; border: none;
  }

  /* scrollbar */
  ::-webkit-scrollbar { width: 4px; height: 4px; }
  ::-webkit-scrollbar-track { background: transparent; }
  ::-webkit-scrollbar-thumb { background: rgba(184,134,11,0.3); }
`;

/* ══════════════════════════════════════════════
   DATA
══════════════════════════════════════════════ */
const RESIDENTS = [
  { id: "R-001", name: "Eleanor Whitfield", age: 84, room: "Suite 3A", level: "Level 2", status: "Stable", since: "Jan 2024", payor: "Private Pay", balance: "$0" },
  { id: "R-002", name: "Harold Jennings", age: 79, room: "Suite 1B", level: "Level 4", status: "Memory Care", since: "Mar 2024", payor: "Medicaid", balance: "$0" },
  { id: "R-003", name: "Margaret Torres", age: 91, room: "Suite 2A", level: "Level 3", status: "Stable", since: "Nov 2023", payor: "VA Benefit", balance: "$240" },
  { id: "R-004", name: "James Caldwell", age: 76, room: "Suite 4C", level: "Level 1", status: "Respite", since: "May 2025", payor: "Private Pay", balance: "$0" },
  { id: "R-005", name: "Dorothy Simmons", age: 88, room: "Suite 2C", level: "Level 4", status: "Memory Care", since: "Jul 2024", payor: "Foundation Aid", balance: "$0" },
];
const STAFF = [
  { name: "Priscilla Monroe", role: "Administrator", shift: "Day", status: "On Duty", cert: "CAL Licensed" },
  { name: "Devon Clarke", role: "Resident Care Manager", shift: "Day", status: "On Duty", cert: "RN, BSN" },
  { name: "Aaliyah Reyes", role: "Memory Care Specialist", shift: "Day", status: "On Duty", cert: "CADDCT" },
  { name: "Marcus Webb", role: "Caregiver", shift: "Evening", status: "Scheduled", cert: "CNA" },
  { name: "Susan Park", role: "Medication Tech", shift: "Day", status: "On Duty", cert: "QMA" },
  { name: "Trevor Mills", role: "Cook", shift: "Day", status: "On Duty", cert: "" },
];
const MESSAGES = [
  { from: "Care Team", body: "Mrs. Whitfield completed her morning physical therapy. Excellent engagement today.", time: "8:42 AM" },
  { from: "Nurse Devon Clarke", body: "Medication schedule updated for Mr. Jennings. Family notified of dosage change per physician order.", time: "10:15 AM" },
  { from: "Administrator Monroe", body: "Quarterly care plan review scheduled for July 12. Please confirm attendance.", time: "Yesterday" },
];
const PROPERTIES = [
  { address: "4821 Sage Ridge Lane", market: "Colorado Springs, CO", beds: 8, status: "Operational", occ: "88%", noi: "$6,400/mo", value: "$620,000" },
  { address: "17 Verona Court", market: "Gilbert, AZ", beds: 10, status: "In Conversion", occ: "—", noi: "—", value: "$740,000" },
  { address: "892 Lakeview Blvd", market: "Round Rock, TX", beds: 12, status: "Under LOI", occ: "—", noi: "—", value: "$810,000" },
];

/* ══════════════════════════════════════════════
   SUB-VIEWS
══════════════════════════════════════════════ */

function FamilyPortal() {
  const [tab, setTab] = useState("overview");
  const tabs = ["overview", "care updates", "messages", "schedule", "billing"];
  return (
    <div className="portal">
      <div className="portal-nav">
        {tabs.map(t => <button key={t} className={`nav-tab ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{t}</button>)}
      </div>
      <div className="portal-content">
        {tab === "overview" && <>
          <div className="hero-strip">
            <div className="section-eyebrow" style={{color:"var(--gold)",marginBottom:"0.4rem"}}>Family Portal</div>
            <h2>Eleanor Whitfield — Suite 3A</h2>
            <p>Graceful Care Home · Level 2 Assisted Living · Private Pay</p>
          </div>
          <div className="stat-row">
            {[["Days Resident","512"],["Care Level","Level 2"],["Last Assessment","June 3"],["Next Review","July 12"],["Status","Stable"]].map(([l,v])=>(
              <div key={l} className="stat-cell"><div className="stat-cell-val">{v}</div><div className="stat-cell-lbl">{l}</div></div>
            ))}
          </div>
          <div className="two-col">
            <div>
              <div className="section-eyebrow">Wellness Overview</div>
              <div className="rule-gold" />
              {[["Mobility","78"],["Nutrition","92"],["Cognitive Engagement","85"],["Social Activity","70"]].map(([l,v])=>(
                <div key={l} className="progress-wrap">
                  <div className="progress-label"><span>{l}</span><span>{v}%</span></div>
                  <div className="progress-bar"><div className="progress-fill" style={{width:`${v}%`}} /></div>
                </div>
              ))}
            </div>
            <div>
              <div className="section-eyebrow">Recent Activity</div>
              <div className="rule-gold" />
              <div className="timeline">
                {[["June 14","Participated in afternoon music circle"],["June 13","Physician check-in — all vitals stable"],["June 12","Physical therapy session completed"],["June 10","Family visit — 2 hours"],["June 8","Monthly medication review"]].map(([d,t])=>(
                  <div key={d} className="timeline-item">
                    <div className="timeline-dot" />
                    <div className="timeline-date">{d}</div>
                    <div className="timeline-text">{t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </>}
        {tab === "care updates" && <>
          <div className="section-header">
            <div className="section-eyebrow">Care Updates</div>
            <div className="section-title">Clinical Log</div>
            <div className="rule-gold" />
          </div>
          {[
            {title:"Quarterly Care Plan Review",date:"June 3, 2025",body:"Mrs. Whitfield's care plan was reviewed with the interdisciplinary team. Level 2 designation maintained. Physical therapy to continue twice weekly. Medication reconciled.",author:"Devon Clarke, RN"},
            {title:"Physician Visit",date:"June 13, 2025",body:"Dr. Okello conducted a routine evaluation. Blood pressure 118/76 — excellent. No medication changes warranted at this time. Follow-up in 30 days.",author:"Dr. A. Okello, MD"},
            {title:"Fall Risk Assessment",date:"May 28, 2025",body:"Routine fall risk screening completed. Score: 14/30 — Low Risk. Non-slip footwear protocol reinforced. Nightlight installed in ensuite per resident request.",author:"Priscilla Monroe, CAL"},
          ].map(c=>(
            <div key={c.title} className="card">
              <div className="card-meta">{c.date} · {c.author}</div>
              <div className="card-title">{c.title}</div>
              <div className="card-body">{c.body}</div>
            </div>
          ))}
        </>}
        {tab === "messages" && <>
          <div className="section-header">
            <div className="section-eyebrow">Secure Messages</div>
            <div className="section-title">Care Team Communication</div>
            <div className="rule-gold" />
          </div>
          <div style={{background:"var(--white)",border:"1px solid var(--border)",marginBottom:"1.5rem"}}>
            {MESSAGES.map((m,i)=>(
              <div key={i} className="message">
                <div className="message-from">{m.from}</div>
                <div className="message-body">{m.body}</div>
                <div className="message-time">{m.time}</div>
              </div>
            ))}
          </div>
          <div className="card">
            <div className="section-eyebrow" style={{marginBottom:"1rem"}}>Send a Message</div>
            <div className="form-group">
              <label className="form-label">To</label>
              <select className="form-select"><option>Care Team</option><option>Administrator</option><option>Nurse on Duty</option></select>
            </div>
            <div className="form-group">
              <label className="form-label">Message</label>
              <textarea className="form-textarea" placeholder="Type your message here…" />
            </div>
            <button className="btn btn-primary">Send Message</button>
          </div>
        </>}
        {tab === "schedule" && <>
          <div className="section-header">
            <div className="section-eyebrow">Activity Schedule</div>
            <div className="section-title">This Week</div>
            <div className="rule-gold" />
          </div>
          {[
            {day:"Monday",items:["8:00 AM — Breakfast & Morning Stretches","10:00 AM — Physical Therapy","2:00 PM — Arts & Crafts Circle","4:00 PM — Garden Walk"]},
            {day:"Tuesday",items:["9:00 AM — Cognitive Games","11:00 AM — Music Therapy","3:00 PM — Family Visit Window"]},
            {day:"Wednesday",items:["10:00 AM — Physical Therapy","1:00 PM — Chair Yoga","3:30 PM — Movie Afternoon"]},
            {day:"Thursday",items:["9:30 AM — Physician Check-In","2:00 PM — Community Bingo","4:00 PM — Scripture & Reflection"]},
            {day:"Friday",items:["10:00 AM — Physical Therapy","12:00 PM — Special Lunch (Italian)","3:00 PM — Music Circle"]},
          ].map(({day,items})=>(
            <div key={day} className="card">
              <div className="card-title">{day}</div>
              <ul style={{paddingLeft:"1rem",marginTop:"0.5rem"}}>
                {items.map(i=><li key={i} style={{fontSize:"0.92rem",color:"var(--muted)",padding:"0.2rem 0"}}>{i}</li>)}
              </ul>
            </div>
          ))}
        </>}
        {tab === "billing" && <>
          <div className="section-header">
            <div className="section-eyebrow">Billing & Financials</div>
            <div className="section-title">Account Summary</div>
            <div className="rule-gold" />
          </div>
          <div className="kpi-grid">
            {[["Monthly Rate","$5,800","Private Pay"],["Balance Due","$0","Current"],["Last Payment","$5,800","June 1"],["Next Due","$5,800","July 1"]].map(([l,v,s])=>(
              <div key={l} className="kpi-card">
                <div className="kpi-label">{l}</div>
                <div className="kpi-value gold">{v}</div>
                <div className="kpi-delta">{s}</div>
              </div>
            ))}
          </div>
          <table className="data-table">
            <thead><tr><th>Date</th><th>Description</th><th>Amount</th><th>Status</th></tr></thead>
            <tbody>
              {[["Jun 1","Monthly Resident Fee — June","$5,800","Paid"],["May 1","Monthly Resident Fee — May","$5,800","Paid"],["Apr 1","Monthly Resident Fee — April","$5,800","Paid"],["Mar 15","Ancillary — Physical Therapy Supplies","$85","Paid"]].map(([d,desc,amt,st])=>(
                <tr key={d+desc}><td>{d}</td><td>{desc}</td><td style={{fontFamily:"'DM Mono',monospace",fontSize:"0.85rem"}}>{amt}</td>
                <td><span className={`badge ${st==="Paid"?"badge-green":"badge-gold"}`}>{st}</span></td></tr>
              ))}
            </tbody>
          </table>
        </>}
      </div>
    </div>
  );
}

function AdminPortal() {
  const [tab, setTab] = useState("dashboard");
  const tabs = ["dashboard","residents","staffing","properties","compliance","ai insights"];
  return (
    <div className="portal">
      <div className="portal-nav">
        {tabs.map(t=><button key={t} className={`nav-tab ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{t}</button>)}
      </div>
      <div className="portal-content">
        {tab === "dashboard" && <>
          <div className="section-header">
            <div className="section-eyebrow">Operations Command</div>
            <div className="section-title">Graceful Care Home — KPI Dashboard</div>
            <div className="section-sub">As of June 15, 2025 · Colorado Springs, CO</div>
            <div className="rule-gold" />
          </div>
          <div className="kpi-grid">
            {[["Occupancy Rate","88%","sage","+3% MoM"],["Revenue / Bed","$5,420","gold","Private Mix"],["Operating Margin","34%","gold","↑ from 29%"],["Fall Rate","0.0","sage","30-Day"],["Med Error Rate","0.0","sage","30-Day"],["Caregiver Retention","91%","gold","YTD"]].map(([l,v,c,d])=>(
              <div key={l} className="kpi-card">
                <div className="kpi-label">{l}</div>
                <div className={`kpi-value ${c}`}>{v}</div>
                <div className="kpi-delta">{d}</div>
              </div>
            ))}
          </div>
          <div className="two-col">
            <div>
              <div className="section-eyebrow">Occupancy by Bed Type</div>
              <div className="rule-gold" />
              {[["Private Pay","3 of 4","75%"],["Memory Care","2 of 2","100%"],["Medicaid / VA","2 of 2","100%"],["Respite (Float)","1 of 2","50%"]].map(([l,c,p])=>(
                <div key={l} className="progress-wrap">
                  <div className="progress-label"><span>{l}</span><span>{c} · {p}</span></div>
                  <div className="progress-bar"><div className="progress-fill green" style={{width:p}} /></div>
                </div>
              ))}
            </div>
            <div>
              <div className="section-eyebrow">Revenue Streams — June</div>
              <div className="rule-gold" />
              {[["Resident Monthly Fees","$29,000"],["Memory Care Premium","$4,400"],["Respite Care","$1,200"],["Adult Day Services","$800"],["VA Assistance","$3,200"],["Foundation Subsidy","($1,800)"]].map(([l,v])=>(
                <div key={l} style={{display:"flex",justifyContent:"space-between",padding:"0.5rem 0",borderBottom:"1px solid rgba(10,10,10,0.06)"}}>
                  <span style={{fontSize:"0.92rem",color:"var(--muted)"}}>{l}</span>
                  <span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.8rem",color:v.startsWith("(")?("var(--red)"):"var(--ink)"}}>{v}</span>
                </div>
              ))}
              <div style={{display:"flex",justifyContent:"space-between",padding:"0.75rem 0",marginTop:"0.25rem",borderTop:"2px solid var(--ink)"}}>
                <span style={{fontFamily:"'DM Mono',monospace",fontSize:"0.7rem",letterSpacing:"0.1em",textTransform:"uppercase",color:"var(--ink)"}}>Gross Revenue</span>
                <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.5rem",color:"var(--gold)"}}>$36,800</span>
              </div>
            </div>
          </div>
        </>}
        {tab === "residents" && <>
          <div className="section-header">
            <div className="section-eyebrow">Resident Management</div>
            <div className="section-title">Current Census</div>
            <div className="rule-gold" />
          </div>
          <div style={{marginBottom:"1rem",display:"flex",gap:"0.75rem",flexWrap:"wrap"}}>
            <button className="btn btn-primary">+ Admit Resident</button>
            <button className="btn btn-outline">Export Census</button>
            <button className="btn btn-outline">Print Care Plans</button>
          </div>
          <table className="data-table">
            <thead><tr><th>ID</th><th>Resident</th><th>Room</th><th>Care Level</th><th>Payor</th><th>Status</th><th>Balance</th></tr></thead>
            <tbody>
              {RESIDENTS.map(r=>(
                <tr key={r.id}>
                  <td style={{fontFamily:"'DM Mono',monospace",fontSize:"0.72rem",color:"var(--faint)"}}>{r.id}</td>
                  <td><strong>{r.name}</strong><br/><span style={{fontSize:"0.8rem",color:"var(--faint)"}}>Age {r.age} · Since {r.since}</span></td>
                  <td style={{fontFamily:"'DM Mono',monospace",fontSize:"0.8rem"}}>{r.room}</td>
                  <td><span className="badge badge-gold">{r.level}</span></td>
                  <td style={{fontSize:"0.85rem",color:"var(--muted)"}}>{r.payor}</td>
                  <td><span className={`badge ${r.status==="Stable"?"badge-green":r.status==="Memory Care"?"badge-gold":"badge-gray"}`}>{r.status}</span></td>
                  <td style={{fontFamily:"'DM Mono',monospace",fontSize:"0.8rem",color:r.balance!=="$0"?"var(--red)":"var(--sage)"}}>{r.balance}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>}
        {tab === "staffing" && <>
          <div className="section-header">
            <div className="section-eyebrow">Workforce Management</div>
            <div className="section-title">Staff Roster</div>
            <div className="rule-gold" />
          </div>
          <div className="kpi-grid" style={{marginBottom:"2rem"}}>
            {[["Staff On Duty","4","sage"],["Scheduled Today","6","gold"],["Open Shifts","1","red"],["OT Risk","Low","sage"]].map(([l,v,c])=>(
              <div key={l} className="kpi-card">
                <div className="kpi-label">{l}</div>
                <div className={`kpi-value ${c}`}>{v}</div>
              </div>
            ))}
          </div>
          <table className="data-table">
            <thead><tr><th>Name</th><th>Role</th><th>Shift</th><th>Status</th><th>Credential</th></tr></thead>
            <tbody>
              {STAFF.map(s=>(
                <tr key={s.name}>
                  <td><strong>{s.name}</strong></td>
                  <td style={{fontSize:"0.88rem",color:"var(--muted)"}}>{s.role}</td>
                  <td style={{fontFamily:"'DM Mono',monospace",fontSize:"0.75rem"}}>{s.shift}</td>
                  <td><span className={`badge ${s.status==="On Duty"?"badge-green":"badge-gray"}`}>{s.status}</span></td>
                  <td style={{fontFamily:"'DM Mono',monospace",fontSize:"0.7rem",color:"var(--faint)"}}>{s.cert||"—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>}
        {tab === "properties" && <>
          <div className="section-header">
            <div className="section-eyebrow">Real Estate Portfolio</div>
            <div className="section-title">Graceful Care Property Holdings</div>
            <div className="rule-gold" />
          </div>
          <div className="kpi-grid" style={{marginBottom:"2rem"}}>
            {[["Total Properties","3","gold"],["Operational Beds","8","sage"],["Portfolio Value","$2.17M","gold"],["Blended NOI","$6,400","sage"]].map(([l,v,c])=>(
              <div key={l} className="kpi-card"><div className="kpi-label">{l}</div><div className={`kpi-value ${c}`}>{v}</div></div>
            ))}
          </div>
          {PROPERTIES.map(p=>(
            <div key={p.address} className="card" style={{borderLeft:"3px solid var(--gold)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"1rem",flexWrap:"wrap"}}>
                <div>
                  <div className="card-title">{p.address}</div>
                  <div className="card-meta">{p.market} · {p.beds} Licensed Beds</div>
                </div>
                <span className={`badge ${p.status==="Operational"?"badge-green":p.status==="In Conversion"?"badge-gold":"badge-gray"}`}>{p.status}</span>
              </div>
              <div style={{display:"flex",gap:"2rem",marginTop:"0.75rem",flexWrap:"wrap"}}>
                {[["Occupancy",p.occ],["Monthly NOI",p.noi],["Estimated Value",p.value]].map(([l,v])=>(
                  <div key={l}><div style={{fontFamily:"'DM Mono',monospace",fontSize:"0.58rem",letterSpacing:"0.15em",color:"var(--faint)",textTransform:"uppercase"}}>{l}</div>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.4rem",color:"var(--gold)"}}>{v}</div></div>
                ))}
              </div>
            </div>
          ))}
        </>}
        {tab === "compliance" && <>
          <div className="section-header">
            <div className="section-eyebrow">Regulatory & Compliance</div>
            <div className="section-title">Compliance Dashboard</div>
            <div className="rule-gold" />
          </div>
          {[
            {area:"State RAL License",status:"Current",exp:"Dec 31, 2025",note:"Annual renewal filed"},
            {area:"Fire Safety Inspection",status:"Current",exp:"Sep 15, 2025",note:"Passed — no deficiencies"},
            {area:"ADA Compliance Audit",status:"Current",exp:"Ongoing",note:"Grab bars installed June 2"},
            {area:"HIPAA Procedures",status:"Current",exp:"Ongoing",note:"Staff training completed May"},
            {area:"Background Checks — All Staff",status:"Current",exp:"Rolling",note:"6 of 6 cleared"},
            {area:"Medication Administration Log",status:"Current",exp:"Daily",note:"0 errors — 30 days"},
            {area:"Care Documentation",status:"Action Needed",exp:"—",note:"2 care plans due for signature"},
          ].map(c=>(
            <div key={c.area} style={{display:"flex",alignItems:"flex-start",justifyContent:"space-between",gap:"1rem",flexWrap:"wrap",padding:"0.85rem 1rem",borderBottom:"1px solid var(--border)",background:"var(--white)"}}>
              <div>
                <div style={{fontWeight:500,marginBottom:"0.15rem"}}>{c.area}</div>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:"0.62rem",color:"var(--faint)"}}>{c.note}</div>
              </div>
              <div style={{textAlign:"right",flexShrink:0}}>
                <span className={`badge ${c.status==="Current"?"badge-green":"badge-red"}`}>{c.status}</span>
                <div style={{fontFamily:"'DM Mono',monospace",fontSize:"0.6rem",color:"var(--faint)",marginTop:"4px"}}>{c.exp}</div>
              </div>
            </div>
          ))}
        </>}
        {tab === "ai insights" && <>
          <div className="section-header">
            <div className="section-eyebrow">AI Operations Intelligence</div>
            <div className="section-title">Predictive Insights</div>
            <div className="section-sub">Powered by Graceful Care AI Engine</div>
            <div className="rule-gold" />
          </div>
          {[
            {module:"Caregiver Scheduling AI",status:"Active",insight:"Evening shift coverage gap detected for July 4 weekend. Recommend: Contact PRN pool now. Projected overtime cost if unresolved: +$420."},
            {module:"Occupancy Forecasting",status:"Active",insight:"Current pipeline shows 2 qualified inquiries likely to convert within 30 days. Projected occupancy: 100% by August 1. Recommend: Begin waitlist procedures."},
            {module:"Resident Wellness Analytics",status:"Active",insight:"Mrs. Torres (Suite 2A) mobility score declined 8 points this week. Suggest: Physical therapy frequency increase and physician notification."},
            {module:"Medication Compliance Alerts",status:"Active",insight:"All residents — 100% medication compliance for the past 30 days. No alerts pending."},
            {module:"Maintenance Prediction",status:"Active",insight:"HVAC unit (Building B) shows elevated cycle frequency. Preventive maintenance recommended within 14 days. Estimated service cost: $280."},
          ].map(m=>(
            <div key={m.module} className="card" style={{borderLeft:`3px solid ${m.status==="Active"?"var(--gold)":"var(--border)"}`}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:"0.5rem"}}>
                <div className="card-title" style={{marginBottom:0}}>{m.module}</div>
                <span className="badge badge-green">{m.status}</span>
              </div>
              <div className="card-body">{m.insight}</div>
            </div>
          ))}
        </>}
      </div>
    </div>
  );
}

function FoundationPortal() {
  const [tab, setTab] = useState("programs");
  const tabs = ["programs","donate","impact","apply for aid"];
  return (
    <div className="portal">
      <div className="portal-nav">
        {tabs.map(t=><button key={t} className={`nav-tab ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{t}</button>)}
      </div>
      <div className="portal-content">
        {tab === "programs" && <>
          <div className="hero-strip">
            <div className="section-eyebrow" style={{color:"var(--gold)",marginBottom:"0.4rem"}}>501(c)(3) Foundation</div>
            <h2>James & Rita Singleton RAL Foundation</h2>
            <p>Providing elder care subsidies, memory care assistance, and caregiver support for those in need.</p>
          </div>
          <div className="three-col">
            {[
              {name:"Graceful Living Scholarship",icon:"🎓",desc:"Subsidizes monthly resident costs for qualifying seniors who cannot afford private-pay rates."},
              {name:"Memory Care Assistance Program",icon:"🧠",desc:"Financial assistance for dementia and Alzheimer's residents and their families."},
              {name:"Caregiver Relief Program",icon:"🤝",desc:"Supports family caregivers with respite, counseling, and emergency financial aid."},
              {name:"Veteran Support Initiative",icon:"🎖️",desc:"Housing and care coordination assistance for veterans and surviving spouses."},
              {name:"Medicaid Gap Program",icon:"⚖️",desc:"Bridges financial gaps for seniors awaiting Medicaid eligibility determinations."},
              {name:"Emergency Placement Program",icon:"🚨",desc:"Rapid placement support for vulnerable seniors facing housing or care crises."},
            ].map(p=>(
              <div key={p.name} className="card" style={{borderTop:"2px solid var(--gold)"}}>
                <div style={{fontSize:"1.8rem",marginBottom:"0.75rem"}}>{p.icon}</div>
                <div className="card-title">{p.name}</div>
                <div className="card-body" style={{marginTop:"0.5rem"}}>{p.desc}</div>
              </div>
            ))}
          </div>
        </>}
        {tab === "donate" && <>
          <div className="section-header">
            <div className="section-eyebrow">Support Our Mission</div>
            <div className="section-title">Give with Purpose</div>
            <div className="section-sub">Every dollar subsidizes dignity for a senior in need.</div>
            <div className="rule-gold" />
          </div>
          <div className="tier-grid">
            <div className="tier-card">
              <div className="tier-name">Companion</div>
              <div className="tier-amount">$50/mo</div>
              <ul className="tier-perks">
                <li>Foundation newsletter</li>
                <li>Impact report quarterly</li>
                <li>Tax-deductible receipt</li>
              </ul>
              <button className="btn btn-outline" style={{marginTop:"1.2rem",width:"100%"}}>Select</button>
            </div>
            <div className="tier-card featured">
              <div className="tier-name">Benefactor</div>
              <div className="tier-amount">$250/mo</div>
              <ul className="tier-perks">
                <li>Named in annual report</li>
                <li>Subsidizes 1 resident/month</li>
                <li>Foundation board invitation</li>
                <li>Site visit opportunity</li>
              </ul>
              <button className="btn btn-gold" style={{marginTop:"1.2rem",width:"100%"}}>Select</button>
            </div>
            <div className="tier-card">
              <div className="tier-name">Legacy</div>
              <div className="tier-amount">$1,000/mo</div>
              <ul className="tier-perks">
                <li>Named room sponsorship</li>
                <li>Advisory committee seat</li>
                <li>Estate planning recognition</li>
                <li>Perpetual impact designation</li>
              </ul>
              <button className="btn btn-outline" style={{marginTop:"1.2rem",width:"100%"}}>Select</button>
            </div>
          </div>
          <div className="closing-block">
            <p>"In honoring our elders, we define the character of our community."</p>
            <div style={{display:"flex",gap:"1rem",justifyContent:"center",flexWrap:"wrap"}}>
              <button className="btn btn-gold">One-Time Gift</button>
              <button className="btn" style={{border:"1px solid rgba(184,134,11,0.4)",color:"var(--gold)",background:"transparent"}}>Planned Giving</button>
            </div>
          </div>
        </>}
        {tab === "impact" && <>
          <div className="section-header">
            <div className="section-eyebrow">Foundation Impact</div>
            <div className="section-title">By the Numbers</div>
            <div className="rule-gold" />
          </div>
          <div className="stat-row">
            {[["Residents Subsidized","12"],["Total Aid Disbursed","$68K"],["Family Caregivers Served","24"],["Veterans Assisted","5"],["Avg Monthly Subsidy","$1,400"]].map(([l,v])=>(
              <div key={l} className="stat-cell"><div className="stat-cell-val">{v}</div><div className="stat-cell-lbl">{l}</div></div>
            ))}
          </div>
          <div className="two-col">
            <div>
              <div className="section-eyebrow">Fund Allocation</div>
              <div className="rule-gold" />
              {[["Resident Subsidies","62%"],["Memory Care Assistance","18%"],["Caregiver Relief","11%"],["Administration","9%"]].map(([l,v])=>(
                <div key={l} className="progress-wrap">
                  <div className="progress-label"><span>{l}</span><span>{v}</span></div>
                  <div className="progress-bar"><div className="progress-fill" style={{width:v}} /></div>
                </div>
              ))}
            </div>
            <div>
              <div className="section-eyebrow">Recent Grants</div>
              <div className="rule-gold" />
              <div className="timeline">
                {[["June 2025","Emergency Placement — $1,800 subsidy authorized for June intake"],["May 2025","Caregiver Relief — $600 respite grant approved for Torres family"],["May 2025","Memory Care Assistance — $1,200 monthly subsidy renewed for 3 residents"],["Apr 2025","Veteran Support — VA coordination secured for Mr. Webb placement"]].map(([d,t])=>(
                  <div key={d+t} className="timeline-item"><div className="timeline-dot" /><div className="timeline-date">{d}</div><div className="timeline-text">{t}</div></div>
                ))}
              </div>
            </div>
          </div>
        </>}
        {tab === "apply for aid" && <>
          <div className="section-header">
            <div className="section-eyebrow">Financial Assistance Application</div>
            <div className="section-title">Apply for Foundation Aid</div>
            <div className="section-sub">Confidential. Reviewed within 5 business days.</div>
            <div className="rule-gold" />
          </div>
          <div className="card">
            <div className="section-eyebrow" style={{marginBottom:"1rem"}}>Applicant Information</div>
            <div className="two-col">
              <div className="form-group"><label className="form-label">Senior's Full Name</label><input className="form-input" placeholder="First Last" /></div>
              <div className="form-group"><label className="form-label">Date of Birth</label><input className="form-input" type="date" /></div>
              <div className="form-group"><label className="form-label">Contact Name</label><input className="form-input" placeholder="Family contact or self" /></div>
              <div className="form-group"><label className="form-label">Relationship</label><select className="form-select"><option>Self</option><option>Spouse</option><option>Adult Child</option><option>Sibling</option><option>Social Worker</option><option>Other</option></select></div>
            </div>
            <div className="form-group"><label className="form-label">Program Requested</label>
              <select className="form-select">
                <option>Graceful Living Scholarship</option>
                <option>Memory Care Assistance Program</option>
                <option>Caregiver Relief Program</option>
                <option>Veteran Support Initiative</option>
                <option>Medicaid Gap Program</option>
                <option>Emergency Placement Program</option>
              </select>
            </div>
            <div className="form-group"><label className="form-label">Describe the Need</label><textarea className="form-textarea" placeholder="Please describe the financial situation and care needs…" /></div>
            <div className="form-group"><label className="form-label">Current Monthly Income</label><input className="form-input" placeholder="$0.00" /></div>
            <div style={{display:"flex",gap:"0.75rem",flexWrap:"wrap",marginTop:"0.5rem"}}>
              <button className="btn btn-primary">Submit Application</button>
              <button className="btn btn-outline">Save Draft</button>
            </div>
          </div>
        </>}
      </div>
    </div>
  );
}

function ProspectPortal() {
  const [tab, setTab] = useState("overview");
  const tabs = ["overview","care options","our homes","schedule a tour","contact"];
  return (
    <div className="portal">
      <div className="portal-nav">
        {tabs.map(t=><button key={t} className={`nav-tab ${tab===t?"active":""}`} onClick={()=>setTab(t)}>{t}</button>)}
      </div>
      <div className="portal-content">
        {tab === "overview" && <>
          <div className="hero-strip">
            <div className="section-eyebrow" style={{color:"var(--gold)",marginBottom:"0.4rem"}}>Prospective Families</div>
            <h2>Aging with Grace, Dignity, and Purpose.</h2>
            <p>A home — not a facility. A community — not a ward. Graceful Care Home.</p>
          </div>
          <div className="stat-row">
            {[["Licensed Beds","10"],["Staff Ratio","1:3"],["Memory Care","Yes"],["Avg Resident Stay","28 mo"],["Family Rating","4.9 ★"]].map(([l,v])=>(
              <div key={l} className="stat-cell"><div className="stat-cell-val">{v}</div><div className="stat-cell-lbl">{l}</div></div>
            ))}
          </div>
          <div className="two-col">
            <div>
              <div className="section-eyebrow">Why Families Choose Us</div>
              <div className="rule-gold" />
              {["Home-like environment — never a clinical institution","Maximum 10 residents — true personalized care","24/7 licensed caregiver supervision","Specialized memory care program","Dedicated family communication portal","Foundation-backed financial assistance available"].map(pt=>(
                <div key={pt} style={{display:"flex",gap:"0.75rem",padding:"0.6rem 0",borderBottom:"1px solid rgba(10,10,10,0.05)"}}>
                  <span style={{color:"var(--gold)",fontSize:"0.8rem",marginTop:"2px"}}>◆</span>
                  <span style={{fontSize:"0.95rem",color:"var(--muted)"}}>{pt}</span>
                </div>
              ))}
            </div>
            <div>
              <div className="section-eyebrow">Frequently Asked Questions</div>
              <div className="rule-gold" />
              {[["What does it cost?","Monthly rates range from $4,200–$6,800 depending on care level. Foundation assistance is available for qualifying residents."],["Is memory care available?","Yes. We operate a dedicated memory care program for Alzheimer's and dementia residents with specialized staff certification."],["How many residents?","We cap at 10 residents per home to ensure meaningful caregiver relationships — never a number, always a name."],["Can we visit before deciding?","Absolutely. We encourage families to schedule a personal tour. Same-week availability in most cases."]].map(([q,a])=>(
                <div key={q} style={{marginBottom:"1rem"}}>
                  <div style={{fontWeight:500,marginBottom:"0.25rem",fontSize:"0.95rem"}}>{q}</div>
                  <div style={{fontSize:"0.9rem",color:"var(--muted)",lineHeight:1.6,fontStyle:"italic"}}>{a}</div>
                </div>
              ))}
            </div>
          </div>
        </>}
        {tab === "care options" && <>
          <div className="section-header">
            <div className="section-eyebrow">Service Lines</div>
            <div className="section-title">Care Options</div>
            <div className="rule-gold" />
          </div>
          {[
            {name:"Traditional Assisted Living",level:"Levels 1–3",icon:"🏡",desc:"Around-the-clock supervised residential care in a beautiful home setting. Includes meals, medication management, housekeeping, laundry, and social activities. Designed for seniors who need support with daily living but are not appropriate for skilled nursing.",services:["24-hour supervision","Medication management","Three meals + snacks daily","Housekeeping & laundry","Mobility assistance","Social & recreational activities","Transportation coordination"]},
            {name:"Memory Care",level:"Level 4",icon:"🧠",desc:"Specialized residential care for individuals living with Alzheimer's disease, dementia, and related cognitive conditions. Secured environment, structured routines, and staff trained in behavioral support.",services:["Certified Memory Care Staff","Secured environment","Cognitive engagement programming","Structured daily routines","Behavior monitoring","Family education & support","Physician coordination"]},
            {name:"Respite Care",level:"Short-Term",icon:"🌿",desc:"Temporary residential care to provide relief for family caregivers or post-hospital transition support. Available by the weekend, week, or month.",services:["Post-hospital recovery","Weekend caregiver relief","Emergency placement","Month-to-month flexibility"]},
            {name:"Adult Day Services",level:"Daytime",icon:"☀️",desc:"Structured daytime programming for seniors who live at home but benefit from supervised activities, socialization, and monitoring during the day.",services:["Supervised daily activities","Meals and nutrition","Social engagement","Health monitoring","Transportation coordination"]},
          ].map(s=>(
            <div key={s.name} className="card" style={{borderTop:"2px solid var(--gold)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"1rem"}}>
                <div>
                  <div style={{fontSize:"1.8rem",marginBottom:"0.5rem"}}>{s.icon}</div>
                  <div className="card-title">{s.name}</div>
                  <div className="card-meta">{s.level}</div>
                  <div className="card-body" style={{marginTop:"0.5rem",marginBottom:"0.75rem"}}>{s.desc}</div>
                  <ul style={{paddingLeft:"1rem"}}>
                    {s.services.map(sv=><li key={sv} style={{fontSize:"0.88rem",color:"var(--muted)",padding:"0.15rem 0"}}>{sv}</li>)}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </>}
        {tab === "our homes" && <>
          <div className="section-header">
            <div className="section-eyebrow">Our Locations</div>
            <div className="section-title">Graceful Care Properties</div>
            <div className="rule-gold" />
          </div>
          {[
            {name:"Graceful Care Home — Sage Ridge",address:"4821 Sage Ridge Lane, Colorado Springs, CO",status:"Accepting Inquiries",beds:"8 Licensed Beds",avail:"2 Beds Available",desc:"A beautifully converted ranch-style home nestled in a quiet Colorado Springs neighborhood. Warm community atmosphere, spacious common areas, private suites, and a fully enclosed garden courtyard."},
            {name:"Graceful Care Home — Verona",address:"17 Verona Court, Gilbert, AZ",status:"Opening Fall 2025",beds:"10 Licensed Beds",avail:"Waitlist Open",desc:"Our newest Arizona location, currently undergoing ADA-compliant conversion. Designed for both traditional assisted living and memory care with dedicated wing separation and modern care infrastructure."},
          ].map(h=>(
            <div key={h.name} className="card" style={{borderLeft:"3px solid var(--gold)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:"1rem",flexWrap:"wrap"}}>
                <div>
                  <div className="card-title">{h.name}</div>
                  <div className="card-meta">{h.address}</div>
                </div>
                <span className={`badge ${h.status==="Accepting Inquiries"?"badge-green":"badge-gold"}`}>{h.status}</span>
              </div>
              <div className="card-body" style={{marginTop:"0.75rem",marginBottom:"0.75rem"}}>{h.desc}</div>
              <div style={{display:"flex",gap:"1.5rem",flexWrap:"wrap"}}>
                <div><div style={{fontFamily:"'DM Mono',monospace",fontSize:"0.58rem",letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--faint)"}}>Licensed Capacity</div><div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.4rem",color:"var(--gold)"}}>{h.beds}</div></div>
                <div><div style={{fontFamily:"'DM Mono',monospace",fontSize:"0.58rem",letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--faint)"}}>Availability</div><div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.4rem",color:"var(--sage)"}}>{h.avail}</div></div>
              </div>
              <div style={{marginTop:"1rem"}}><button className="btn btn-primary">Schedule a Tour</button></div>
            </div>
          ))}
        </>}
        {tab === "schedule a tour" && <>
          <div className="section-header">
            <div className="section-eyebrow">Personal Tour Request</div>
            <div className="section-title">We'd love to meet you.</div>
            <div className="section-sub">Tours are typically available within 48 hours of request.</div>
            <div className="rule-gold" />
          </div>
          <div className="card">
            <div className="two-col">
              <div className="form-group"><label className="form-label">Your Name</label><input className="form-input" placeholder="Full name" /></div>
              <div className="form-group"><label className="form-label">Phone</label><input className="form-input" placeholder="(000) 000-0000" /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" placeholder="email@example.com" /></div>
              <div className="form-group"><label className="form-label">Relationship to Senior</label><select className="form-select"><option>Adult Child</option><option>Spouse</option><option>Self</option><option>Sibling</option><option>Social Worker / Case Manager</option><option>Other</option></select></div>
              <div className="form-group"><label className="form-label">Preferred Location</label><select className="form-select"><option>Sage Ridge — Colorado Springs</option><option>Verona — Gilbert, AZ</option><option>No preference</option></select></div>
              <div className="form-group"><label className="form-label">Preferred Date</label><input className="form-input" type="date" /></div>
            </div>
            <div className="form-group"><label className="form-label">Care Needs (Brief Summary)</label><textarea className="form-textarea" placeholder="Tell us a little about your loved one's situation and what you're looking for…" /></div>
            <div style={{display:"flex",gap:"0.75rem",flexWrap:"wrap"}}>
              <button className="btn btn-primary">Request Tour</button>
              <button className="btn btn-outline">Call Us Instead</button>
            </div>
          </div>
        </>}
        {tab === "contact" && <>
          <div className="section-header">
            <div className="section-eyebrow">Get in Touch</div>
            <div className="section-title">We're here for you.</div>
            <div className="rule-gold" />
          </div>
          <div className="two-col">
            <div>
              {[["General Inquiries","info@gracefulcarehome.com"],["Admissions","admissions@gracefulcarehome.com"],["Foundation","foundation@gracefulcarehome.com"],["Administrator (Direct)","(719) 555-0142"]].map(([l,v])=>(
                <div key={l} style={{padding:"0.9rem 0",borderBottom:"1px solid var(--border)"}}>
                  <div style={{fontFamily:"'DM Mono',monospace",fontSize:"0.6rem",letterSpacing:"0.14em",textTransform:"uppercase",color:"var(--faint)",marginBottom:"0.2rem"}}>{l}</div>
                  <div style={{fontFamily:"'EB Garamond',serif",fontSize:"1.05rem",color:"var(--gold)"}}>{v}</div>
                </div>
              ))}
              <div style={{marginTop:"1.5rem"}}>
                <div className="section-eyebrow">Office Hours</div>
                <div className="rule-gold" />
                <div style={{fontSize:"0.95rem",color:"var(--muted)"}}>Monday – Friday: 8:00 AM – 6:00 PM<br/>Saturday: 9:00 AM – 3:00 PM<br/>Sunday: By Appointment</div>
              </div>
            </div>
            <div className="card">
              <div className="section-eyebrow" style={{marginBottom:"1rem"}}>Send a Message</div>
              <div className="form-group"><label className="form-label">Name</label><input className="form-input" placeholder="Your name" /></div>
              <div className="form-group"><label className="form-label">Email</label><input className="form-input" type="email" placeholder="your@email.com" /></div>
              <div className="form-group"><label className="form-label">Subject</label><select className="form-select"><option>Admissions Inquiry</option><option>Tour Request</option><option>Financial Assistance</option><option>Foundation / Donation</option><option>Media / Press</option><option>Other</option></select></div>
              <div className="form-group"><label className="form-label">Message</label><textarea className="form-textarea" placeholder="How can we help?" /></div>
              <button className="btn btn-primary">Send Message</button>
            </div>
          </div>
        </>}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════
   ROOT APP
══════════════════════════════════════════════ */
const FUNNELS = [
  { key: "family", icon: "🏡", label: "Families & Residents", name: "Family Portal", desc: "View care updates, communicate with the team, access schedules and billing for your loved one." },
  { key: "admin", icon: "⚙️", label: "Administrators & Operators", name: "Operations Center", desc: "KPI dashboard, resident census, staffing, property portfolio, compliance, and AI insights." },
  { key: "foundation", icon: "🤝", label: "Foundation & Donors", name: "Foundation Portal", desc: "Explore giving programs, apply for financial assistance, review impact reports, and donate." },
  { key: "prospect", icon: "🌿", label: "Prospective Residents", name: "Prospective Families", desc: "Learn about our care options, tour our homes, and take the first step toward placement." },
];

export default function App() {
  const [portal, setPortal] = useState(null);
  const selected = FUNNELS.find(f => f.key === portal);

  return (
    <>
      <style>{CSS}</style>
      {!portal ? (
        <div className="landing">
          <div className="landing-inner">
            <div className="landing-rule" />
            <div className="landing-eyebrow">GracefulCareHome.com</div>
            <div className="landing-title">Graceful<br/><em>Care Home</em></div>
            <div className="landing-tagline">Aging with Grace, Dignity, and Purpose.</div>
            <div className="landing-divider"><span>Select Your Portal</span></div>
            <div className="funnels">
              {FUNNELS.map(f => (
                <button key={f.key} className="funnel-card" onClick={() => setPortal(f.key)}>
                  <span className="funnel-arrow">→</span>
                  <span className="funnel-icon">{f.icon}</span>
                  <div className="funnel-label">{f.label}</div>
                  <div className="funnel-name">{f.name}</div>
                  <div className="funnel-desc">{f.desc}</div>
                </button>
              ))}
            </div>
            <div style={{marginTop:"2rem",fontFamily:"'DM Mono',monospace",fontSize:"0.6rem",letterSpacing:"0.15em",color:"rgba(254,254,254,0.2)",textTransform:"uppercase"}}>
              James & Rita Singleton RAL Foundation · Est. 2024 · GracefulCareHome.com
            </div>
          </div>
        </div>
      ) : (
        <div className="portal">
          <div className="portal-header">
            <div className="portal-wordmark">
              <span className="portal-wordmark-text">Graceful Care Home</span>
              <span className="portal-wordmark-badge">{selected?.label}</span>
            </div>
            <button className="portal-back" onClick={() => setPortal(null)}>← All Portals</button>
          </div>
          {portal === "family" && <FamilyPortal />}
          {portal === "admin" && <AdminPortal />}
          {portal === "foundation" && <FoundationPortal />}
          {portal === "prospect" && <ProspectPortal />}
          <div className="portal-footer">
            <span className="footer-copy">© 2025 Graceful Care Home Operations LLC · James & Rita Singleton RAL Foundation 501(c)(3)</span>
            <div className="footer-links">
              <button className="footer-link" onClick={()=>setPortal(null)}>All Portals</button>
              <button className="footer-link">Privacy</button>
              <button className="footer-link">HIPAA</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
